import { useState, useMemo } from 'react';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { DataFileTct } from '@dealstack/shared/lib/generated/models/DataFileTct';
import { useInvestorsController } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/Investors/useInvestorsController';
import {
	CreateDocumentWorkflowDto,
	CreateDocumentWorkflowsDto,
	DataSimpleInvestorTct,
	DocumentWorkflowFileDto,
} from '@dealstack/shared/lib/generated';

import {
	findInvestorByName,
	findInvestorByEmail,
	findInvestorById,
} from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/utils';
import { InvestorFileMapping } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/SimpleDocumentWorkflowBatch/types';

import {
	createSimpleDocumentWorkflowDocuments,
	uploadContractTemplateToGodMode,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { SDWDocumentCharacteristicsController } from './sdwdocumentCharacteristics';
import * as Sentry from '@sentry/react';

export const useSimpleDocumentWorkflowBatchController = (mipId: string, dealId: string) => {
	/**************VARS***************** */

	//UI State
	const [isInvestorSelectModalOpen, setIsInvestorSelectModalOpen] = useState(false);

	//Source State
	const [files, setFiles] = useState<DataFileTct[]>([]);
	const [manualMappings, setManualMappings] = useState<Record<string, DataSimpleInvestorTct>>({});

	const [selectedMissingInvestorFile, setSelectedMissingInvestorFile] =
		useState<InvestorFileMapping | null>(null);

	const toastApi = useDSToast();

	const uploadWorkflowRequest = useWrappedRequest(
		uploadContractTemplateToGodMode,
		{},
		{
			onSuccessDefault: () => {
				onTapResetWorkflow();
			},
			onLocalErrorDefault: ({ error }) => {
				logger.error(`Workflow upload error: ${error}`);
			},
		}
	);

	const createDocsRequest = useWrappedRequest(
		createSimpleDocumentWorkflowDocuments,
		{},
		{
			onSuccessDefault: () => {
				onTapResetWorkflow();
			},
			onLocalErrorDefault: ({ error }) => {
				logger.error(`Workflow upload error: ${error}`);
			},
		}
	);

	/**************imports***************** */

	const { investorListData } = useInvestorsController(mipId);
	const documentCharacteristicsController = SDWDocumentCharacteristicsController();

	/**************DERIVED STATE***************** */

	const investorList = useMemo(() => investorListData ?? [], [investorListData]);

	/**
	 * Compute matched and missing investor file mappings from source state.
	 * Combines auto-matching (by file name) with manual assignments.
	 */
	const { investorListWithFiles, missingInvestorFiles } = useMemo(() => {
		if (files.length === 0 || investorList.length === 0) {
			return {
				investorListWithFiles: [] as InvestorFileMapping[],
				missingInvestorFiles: [] as InvestorFileMapping[],
			};
		}

		const matched: InvestorFileMapping[] = [];
		const missing: InvestorFileMapping[] = [];

		for (const file of files) {
			// Check manual mapping first
			const manualInvestor = manualMappings[file.id];
			if (manualInvestor) {
				matched.push({ investor: manualInvestor, file });
				continue;
			}

			// Try auto-match: name → email → userId
			const investor =
				findInvestorByName(investorList, file.name) ??
				findInvestorByEmail(investorList, file.name) ??
				findInvestorById(investorList, file.name);

			if (investor) {
				matched.push({ investor, file });
			} else {
				logger.info('investor not found for file', file.name);
				missing.push({ file, investor: undefined });
			}
		}

		return { investorListWithFiles: matched, missingInvestorFiles: missing };
	}, [files, investorList, manualMappings]);

	/**************METHODS***************** */

	/**
	 * Sets the files in the controller once returned from the component
	 * @param files - The files to set
	 */
	const setDroppedFiles = (files: DataFileTct[]) => {
		logger.info(
			useSimpleDocumentWorkflowBatchController.name,
			'setting the files in controller',
			files
		);
		setFiles(files);
		setManualMappings({});
	};

	const onTapResetWorkflow = () => {
		setFiles([]);
		setManualMappings({});
		setSelectedMissingInvestorFile(null);
		setIsInvestorSelectModalOpen(false);
		documentCharacteristicsController.reset();
	};

	/*
	 *The final step to upload the batch
	 *
	 * Creates the workflow documents and submissions
	 *
	 * 1. Upload all the workflow file templates
	 * 2. Create the workflow documents and submissions
	 *
	 */
	const onTapCreateSimpleDocumentWorkflow = async () => {
		if (investorListWithFiles.length === 0) {
			return;
		}

		const buffer: InvestorFileMapping[] = [];
		//STEP 1: upload all the workflowfile templates
		for (const row of investorListWithFiles) {
			const res = await _uploadWorkflowFileTemplate(row.file);
			row.documentWorkflowFileId = res?.payload?.id;
			buffer.push(row);
		}

		//Step2: Create the workflow documents and submissions
		//NOTE: (@andrew-ho-DealStack) done a second loop here. Could do this in same loop above but felt cleaner
		//to upload files then handle the workflows
		let errorCount = 0;
		const errorInvestors: string[] = [];
		for (const row of buffer) {
			const res = await _createSimpleDocumentWorkflowDocuments(row);
			if (!res?.payload) {
				errorCount++;
				errorInvestors.push(
					`${row.investor?.user.firstName} ${row.investor?.user.lastName}`
				);
				Sentry.captureException(res);
			}
		}

		if (errorCount > 0) {
			toastApi.error({
				message: `There were ${errorCount} errors creating the workflow documents: Please check the docs for ${errorInvestors.join(
					', '
				)}`,
			});
		} else {
			toastApi.success({
				message: `All Workflow documents created successfully`,
			});
		}

		//STEP 3: RESET And clear state
		onTapResetWorkflow();
	};

	const onTapMissingRow = (row: InvestorFileMapping) => {
		setIsInvestorSelectModalOpen(true);
		setSelectedMissingInvestorFile(row);
	};

	const closeInvestorSelectModal = () => {
		setIsInvestorSelectModalOpen(false);
	};

	const onSelectInvestorInMissingRow = (investor: DataSimpleInvestorTct) => {
		if (!selectedMissingInvestorFile) {
			toastApi.error({ message: 'No missing investor file selected' });
			return;
		}

		setManualMappings((prev) => ({
			...prev,
			[selectedMissingInvestorFile.file.id]: investor,
		}));

		setSelectedMissingInvestorFile(null);
		setIsInvestorSelectModalOpen(false);
	};

	/**************PRIVATE*********  ******** */

	/***
	 *  Upload a file to db to become a workflow file template
	 *
	 */
	const _uploadWorkflowFileTemplate = async (file: DataFileTct) => {
		const body: DocumentWorkflowFileDto = {
			fileId: file.id,
		};

		const res = await uploadWorkflowRequest
			.fetch({
				mipId,
				dealId,
				workflowFileDto: body,
			})
			.concludeWithPromise();

		return res;
	};

	/**
	 * Note: this endpoint allows for multiple investors with multiple of the same document
	 *
	 * this workflow is for 1 investor with 1 document.
	 *
	 * If users want to to do 1 investor with multiple documents, they can use the exisiting UI in the main production app
	 *
	 *
	 * @param docForInvestorWithWorkflowFileId
	 * @returns
	 */
	const _createSimpleDocumentWorkflowDocuments = async (
		docForInvestorWithWorkflowFileId: InvestorFileMapping
	) => {
		if (!docForInvestorWithWorkflowFileId.documentWorkflowFileId) {
			throw new Error('Document workflow file id is required');
		}
		if (!docForInvestorWithWorkflowFileId.investor?.id) {
			throw new Error('Investor id is required');
		}

		const document: CreateDocumentWorkflowDto = {
			documentWorkflowFileId: docForInvestorWithWorkflowFileId.documentWorkflowFileId,
			templateType: documentCharacteristicsController.templateType,
			requiredWitnesses: documentCharacteristicsController.requiredWitnesses,
			signatureType: documentCharacteristicsController.signatureType,
			isSignatureAdvanced: documentCharacteristicsController.isSignatureAdvanced,
			individualScheduleRequired:
				documentCharacteristicsController.individualScheduleRequired,
			isToBeSubmitted: documentCharacteristicsController.isToBeSubmitted,
			emailText: documentCharacteristicsController.emailText || null,
			guidanceNoteFileId: documentCharacteristicsController.guidanceNoteFileId || null,
			counterparties: documentCharacteristicsController.counterparties,
		};

		const body: CreateDocumentWorkflowsDto = {
			documents: [document],
			selectedIds: [docForInvestorWithWorkflowFileId.investor.id || ''],
		};

		const res = await createDocsRequest
			.fetch({
				createDocumentWorkflowsDto: body,
				mipId,
				dealId,
			})
			.concludeWithPromise();
		return res;
	};

	/**************RETURN*********  ******** */
	return {
		//NOTE:UI STATE
		isInvestorSelectModalOpen,

		//NOTE:STATE
		files,
		investorList,
		investorListWithFiles,
		missingInvestorFiles,
		//NOTE:METHODS
		setDroppedFiles,
		onTapResetWorkflow,
		onTapCreateSimpleDocumentWorkflow,
		onTapMissingRow,
		closeInvestorSelectModal,
		onSelectInvestorInMissingRow,

		//NOTE:CONTROLLERS
		documentCharacteristicsController,
	};
};
